import styles from './index.module.scss';
import { PropType, defineComponent, h, onMounted, ref, computed } from 'vue';
import { ID, from } from '@phi/newtype';
import { Option } from '@vaaas/fpts/option';
import ListGroupSelect from '@components/ListGroupSelect';
import { Document } from '@phi/onshape';
import OnshapeDocumentThumbnail from '@features/onshape-thumbnails/components/OnshapeDocumentThumbnail';
import { FormText } from '@components/form';
import { PaginatedOnshapeDocuments } from '@features/list-onshape-documents';
import { debounce } from '@vaaas/fpts/timing';
import { LoadingIndicator, utility } from '@phi/components';
import { Flex } from '@phi/components/layout';

export default defineComponent({
    props: {
        document: {
            required: false,
            type: null as any as PropType<ID<'document'>>,
        },

        workspace: {
            required: false,
            type: null as any as PropType<ID<'workspace'>>,
        },

        onshape_documents_store: {
            required: false,
            type: PaginatedOnshapeDocuments,
            default: () => new PaginatedOnshapeDocuments(),
        },
    },

    emits: {
        'update:document': (x: Option<ID<'document'>>) => true,
        'update:workspace': (x: Option<ID<'workspace'>>) => true,
    },

    setup(props, ctx) {
        const { onshape_documents_store } = props;
        const DocumentSelect = ListGroupSelect<Document>();

        const selected_document = computed(() =>
            onshape_documents_store.items.find(x =>
                x.id === props.document
                && x.defaultWorkspace.id === props.workspace
            ));

        const filtered_documents = computed(() =>
            onshape_documents_store.items.filter(x => x.name.toLowerCase().includes(searchText.value.toLowerCase()))
        );

        onMounted(() => {
            if (!onshape_documents_store.has_loaded())
                onshape_documents_store.more();
        });

        const searchText = ref('');

        function renderer(x: Document) {
            return h(Flex, {
                key: from(x.id),
                align: 'center',
                direction: 'row',
                class: [utility.left, utility.no_interact],
            }, () => [
                h(OnshapeDocumentThumbnail, {
                    class: styles.thumbnail,
                    document: x.id,
                    workspace: x.defaultWorkspace.id,
                }),

                h('span', {
                    class: 'name'
                }, x.name),
            ]);
        }

        function scrolled_to_bottom() {
            if (searchText.value.length)
                onshape_documents_store.search(searchText.value);
            else
                onshape_documents_store.more();
        }

        function search(x: string) {
            searchText.value = x;
            onshape_documents_store.search(searchText.value);
        }

        return () => h('div', {
            class: [
                'onshape-document-explorer',
                utility.margin_gap_3,
            ],
        }, [
            h('header', [
                h(FormText, {
                    placeholder: 'Search Onshape Documents',
                    value: searchText.value,
                    'onUpdate:value': debounce(search, 500),
                }),
            ]),
            h(DocumentSelect, {
                value: selected_document.value,
                'onUpdate:value': (x) => {
                    ctx.emit('update:document', x?.id);
                    ctx.emit('update:workspace', x?.defaultWorkspace?.id);
                },
                onBottom: scrolled_to_bottom,
                items:
                    filtered_documents.value
                        .map(x => ({
                            value: x,
                            node: renderer(x),
                        })),
            }),
            LoadingIndicator(!onshape_documents_store.fetching),
        ]);
    },
});
